import { Suspense } from 'react';
import type { JSONContent } from '@tiptap/core';
import { useSuspenseQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LibraryLayout } from '../common/LibraryLayout';
import { ErrorBoundary } from 'react-error-boundary';
import { snippetsQueries } from 'services/content/snippetsQueries';
import { useFolderNavigation } from '../common/useFolderNavigation';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import { SearchAssetsInput } from '../common/SearchAssetsInput';
import { LibraryEmptySearchResults } from '../common/LibraryEmptySearchResults';
import { ROOT_ID } from '../common/folderNavigation';
import { useLibrarySearch } from '../common/useLibrarySearch';
import { openNotification } from 'components/notification';
import { useTranslation } from 'react-i18next';
import { folderQueries } from 'services/folders/folderQueries';

type SnippetsProps = {
  onInsertSnippet: (content: JSONContent) => void;
};

function SnippetsContent({ onInsertSnippet }: SnippetsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: rootFolder } = useSuspenseQuery(folderQueries.listFolders('SNIPPET'));
  const { breadcrumbs, currentFolder, selectFolder } = useFolderNavigation(rootFolder, 'All');

  const insertSnippetMutation = useMutation({
    mutationFn: async (contentId: string) => {
      return queryClient.fetchQuery(snippetsQueries.getById(contentId));
    },
    onSuccess: (snippet) => {
      onInsertSnippet(snippet.content);
    },
    onError: () => {
      openNotification({
        type: 'error',
        title: t('library.insert_snippet_error_title'),
      });
    },
  });

  const { searchTerm, setSearchTerm, folders, contents, handleFolderSelect, isAtRoot, isSearchEmpty } = useLibrarySearch(
    rootFolder,
    currentFolder,
    selectFolder
  );

  return (
    <LibraryLayout.Container>
      <SearchAssetsInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} sx={{ width: '100%' }} />

      {breadcrumbs.length > 1 && !searchTerm && (
        <LibraryLayout.Navigation>
          <Breadcrumbs items={breadcrumbs} onSelectPath={handleFolderSelect} />
        </LibraryLayout.Navigation>
      )}

      {isSearchEmpty ? (
        <LibraryEmptySearchResults />
      ) : (
        <>
          {folders.length > 0 && (
            <LibraryLayout.Section title="Folders">
              {folders.map((folder) => (
                <LibraryLayout.FolderListItem key={folder.id ?? ROOT_ID} onClick={() => handleFolderSelect(folder.id)}>
                  {folder.name}
                </LibraryLayout.FolderListItem>
              ))}
            </LibraryLayout.Section>
          )}

          {contents.length > 0 && (
            <LibraryLayout.Section title={folders.length === 0 ? '' : 'Snippets'}>
              {contents.map((content) => (
                <LibraryLayout.AssetListItem
                  key={content.id}
                  onClick={() => insertSnippetMutation.mutate(content.id)}
                  folderName={isAtRoot && searchTerm ? content.folderName : undefined}
                >
                  {content.name}
                </LibraryLayout.AssetListItem>
              ))}
            </LibraryLayout.Section>
          )}
        </>
      )}
    </LibraryLayout.Container>
  );
}

export function Snippets({ onInsertSnippet }: SnippetsProps) {
  return (
    <Suspense
      fallback={
        <LibraryLayout.Container>
          <LibraryLayout.Section title="Folders">
            <LibraryLayout.FolderListItem isLoading />
            <LibraryLayout.FolderListItem isLoading />
            <LibraryLayout.FolderListItem isLoading />
          </LibraryLayout.Section>

          <LibraryLayout.Section title="Snippets">
            <LibraryLayout.AssetListItem isLoading />
            <LibraryLayout.AssetListItem isLoading />
            <LibraryLayout.AssetListItem isLoading />
          </LibraryLayout.Section>
        </LibraryLayout.Container>
      }
    >
      <ErrorBoundary fallback={null}>
        <SnippetsContent onInsertSnippet={onInsertSnippet} />
      </ErrorBoundary>
    </Suspense>
  );
}
